/**
 * The contribution arc: your voice (a single slice) becomes our voices (the
 * whole pie, your slice sliding in) which sets change in motion (the pie turns
 * into a rolling wheel). Hand-drawn inline SVGs in the app's line-icon style.
 *
 * `count` is the number of completed contributions ("voices"). null while it
 * loads; the count line simply stays hidden until it arrives.
 */

type VoiceArcProps = { count: number | null };

export function VoiceArc({ count }: VoiceArcProps) {
  return (
    <div className="w-full flex flex-col items-center gap-5">
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-1.5">
        <Beat icon={<SliceIcon />} label="Your voice" caption="one perspective" />
        <Connector />
        <Beat icon={<PieIcon />} label="Our voices" caption="joined together" />
        <Connector />
        <Beat icon={<WheelIcon />} label="Lead change" caption="set in motion" />
      </div>
      <CountLine count={count} />
    </div>
  );
}

function Beat({ icon, label, caption }: { icon: React.ReactNode; label: string; caption: string }) {
  return (
    <div className="flex flex-col items-center text-center w-28">
      <div className="text-ink-900 mb-2">{icon}</div>
      <span className="font-display text-base text-ink-900 leading-tight">{label}</span>
      <span className="text-[11px] uppercase tracking-[0.14em] text-ink-300 mt-0.5">{caption}</span>
    </div>
  );
}

function Connector() {
  return (
    <>
      {/* right chevron on wide screens */}
      <svg
        className="hidden sm:block text-ink-300 shrink-0 mb-7"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="m9 6 6 6-6 6" />
      </svg>
      {/* down chevron when stacked */}
      <svg
        className="block sm:hidden text-ink-300 shrink-0"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </>
  );
}

function CountLine({ count }: { count: number | null }) {
  if (count === null) return null;
  if (count <= 0) {
    return <p className="text-ink-500 leading-relaxed">Be the first to join the conversation.</p>;
  }
  return (
    <p className="text-ink-500 leading-relaxed">
      <span className="font-display text-ink-900">{count.toLocaleString()}</span>{" "}
      {count === 1 ? "has" : "have"} joined the conversation.
    </p>
  );
}

// ===================== icons =====================
// All centered on (12,12), radius 8.5, matching the app's stroke-icon style.

const ICON = {
  width: 44,
  height: 44,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

// One slice — a single contribution. A faint full circle (the whole) with one
// slice picked out and filled, so it clearly reads as a piece OF the pie.
function SliceIcon() {
  return (
    <svg {...ICON}>
      <circle cx="12" cy="12" r="8.5" strokeOpacity={0.25} />
      <path
        d="M12 12 L12 3.5 A8.5 8.5 0 0 1 19.36 16.25 Z"
        fill="currentColor"
        fillOpacity={0.2}
      />
      <line x1="12" y1="12" x2="12" y2="3.5" />
      <line x1="12" y1="12" x2="19.36" y2="16.25" />
    </svg>
  );
}

// The whole pie, complete, with one slice shaded — your slice, now part of it.
function PieIcon() {
  return (
    <svg {...ICON}>
      <circle cx="12" cy="12" r="8.5" />
      {/* slice dividers: three radii make a readable pie */}
      <line x1="12" y1="12" x2="12" y2="3.5" />
      <line x1="12" y1="12" x2="19.36" y2="16.25" />
      <line x1="12" y1="12" x2="4.64" y2="16.25" />
      {/* the top slice, shaded in place (not detached) */}
      <path
        d="M12 12 L12 3.5 A8.5 8.5 0 0 1 19.36 16.25 Z"
        fill="currentColor"
        fillOpacity={0.18}
      />
    </svg>
  );
}

// The pie becomes a rolling wheel — change set in motion.
function WheelIcon() {
  return (
    <svg {...ICON}>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="2.4" />
      {/* spokes: three diameters */}
      <line x1="12" y1="3.5" x2="12" y2="20.5" />
      <line x1="19.36" y1="7.75" x2="4.64" y2="16.25" />
      <line x1="19.36" y1="16.25" x2="4.64" y2="7.75" />
      {/* motion hint */}
      <path d="M1.5 9.5q-1 2.5 0 5" strokeOpacity={0.5} />
    </svg>
  );
}
